import {
    isValidElement,
    cloneElement,
    useEffect,
    useState,
    useRef,
    type FC
} from "react";
import {
    type ViewStyle,
    StyleSheet,
    Pressable,
    Platform,
    View
} from "react-native";
import type IHoverCardTriggerProps from "./type";
import type {
    HoverCardTriggerKeyboardEventType,
    HoverCardTriggerChildPropsType,
    HoverCardTriggerWebPropsType
} from "./type";
import stylesheet, {
    HOVER_CARD_TRIGGER_VARIANT,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useInteractionState
} from "../../../../helpers/interaction";
import {
    useHoverCardContext
} from "../../context";
import {
    getPopoverTabbables,
    focusPopoverElement,
    getPopoverElement
} from "../../../popover/util";
import {
    getFocusRingStyle
} from "../../../../helpers/theme/state";
import StateLayer from "../../../stateLayer";
import Text from "../../../text";

const HoverCardTrigger: FC<IHoverCardTriggerProps> = ({
    customTheme: customThemeProp,
    children,
    onPress,
    title,
    style
}) => {
    const context = useHoverCardContext({
        part: "Trigger"
    });

    const customTheme = customThemeProp ?? context?.customTheme;

    const {
        shapeConfigs,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const [
        isPointerInside,
        setIsPointerInside
    ] = useState(false);

    const isFirstRunRef = useRef(true);

    const {
        interactionHandlers,
        isFocusVisible,
        isHovered,
        isPressed
    } = useInteractionState();

    const {
        link: linkDynamicStyle
    } = useStyles({
        shapeConfigs,
        radiuses,
        spaces
    });

    const isIntent = isPointerInside || isFocusVisible;

    useEffect(() => {
        if(isFirstRunRef.current) {
            isFirstRunRef.current = false;

            return;
        }

        if(!context?.isEnabled) {
            return;
        }

        if(isIntent) {
            context.handleScheduleOpen();

            return;
        }

        context.handleScheduleClose();
    }, [
        isIntent
    ]);

    useEffect(() => {
        if(context) {
            context.targetPressRef.current = onPress;
        }
    });

    if(!context) {
        return null;
    }

    const {
        handleSheetOpen,
        touchBehaviour,
        handleClose,
        surfaceRef,
        anchorRef,
        summaryId,
        isOpen,
        summary
    } = context;

    const childElement = isValidElement<HoverCardTriggerChildPropsType>(children) ? children : null;

    const label = title ?? (typeof children === "string" ? children : undefined);

    const isLongPressEnabled = Platform.OS !== "web" && touchBehaviour === "longPress";

    const describedBy = Platform.OS === "web" && summary ? summaryId : undefined;

    const handlePress = () => {
        childElement?.props.onPress?.();

        onPress?.();

        handleClose();
    };

    const onKeyDown = (event: HoverCardTriggerKeyboardEventType) => {
        const surfaceElement = getPopoverElement(surfaceRef.current);

        if(event.key !== "Tab" || event.shiftKey || !isOpen || !surfaceElement) {
            return;
        }

        const [
            firstElement
        ] = getPopoverTabbables(surfaceElement);

        if(!firstElement) {
            return;
        }

        event.preventDefault();

        focusPopoverElement(firstElement);
    };

    const renderTrigger = () => {
        if(childElement) {
            return cloneElement(childElement, {
                accessibilityHint: Platform.OS === "web" ? undefined : summary,
                onLongPress: isLongPressEnabled ? handleSheetOpen : undefined,
                "aria-describedby": describedBy,
                onPress: handlePress
            });
        }

        return <Pressable
            {...interactionHandlers}
            accessibilityHint={Platform.OS === "web" ? undefined : summary}
            onLongPress={isLongPressEnabled ? handleSheetOpen : undefined}
            aria-describedby={describedBy}
            style={[
                stylesheet.link,
                linkDynamicStyle,
                getFocusRingStyle({
                    isFocusVisible,
                    borders,
                    colors
                })
            ]}
            onPress={handlePress}
            role="link"
        >
            <StateLayer
                isHovered={(isHovered || isOpen) && !isPressed}
                borderRadius={radiuses.xs}
                borderWidth={borders.line}
                customTheme={customTheme}
                overlayColor="primary"
                isPressed={isPressed}
            />
            <Text
                variant={HOVER_CARD_TRIGGER_VARIANT}
                customTheme={customTheme}
                style={stylesheet.title}
                color="link"
            >
                {label}
            </Text>
        </Pressable>;
    };

    const webProps: HoverCardTriggerWebPropsType = Platform.OS === "web" ? {
        onKeyDown
    } : {};

    const flatStyle = StyleSheet.flatten<ViewStyle>(style);

    const isAnchorStretched = flatStyle?.alignSelf === "stretch" || flatStyle?.width !== undefined;

    return <View
        style={style}
    >
        <View
            {...webProps}
            onFocus={childElement ? interactionHandlers.onFocus : undefined}
            onBlur={childElement ? interactionHandlers.onBlur : undefined}
            style={[
                stylesheet.anchor,
                isAnchorStretched ? stylesheet.stretchedAnchor : undefined
            ]}
            onPointerLeave={() => setIsPointerInside(false)}
            onPointerEnter={() => setIsPointerInside(true)}
            collapsable={false}
            ref={anchorRef}
        >
            {renderTrigger()}
        </View>
    </View>;
};
export default HoverCardTrigger;
